import queryClient from '@/api/queryClient';
import { orpc } from '@/api/rpc/client';
import { Action, Subject, AbilityRulesDTO } from '@erp/authz';
import { AuthzCanOutput } from '@erp/rpc';
import { QueryClient } from '@tanstack/react-query';

export const AUTHZ_CACHE_TIME_MS = 5 * 60 * 1000; // 5 minutes

export function getAuthzQueryKey({
  action,
  subject,
  record,
}: {
  action: Action;
  subject: Subject;
  record?: Record<string, unknown>;
}) {
  return [
    'authz',
    ...orpc.authz.can.queryKey({
      input: { action, subject, record },
    }),
  ];
}

class AuthzClient {
  private queryClient: QueryClient;
  private initialized = false;

  constructor(queryClient: QueryClient) {
    this.queryClient = queryClient;
  }

  /**
   * Reset the authorization cache and initialization state.
   * Should be called when a user logs out or when switching users
   * to prevent permission leakage between user sessions.
   */
  reset(): void {
    this.queryClient.removeQueries({ queryKey: ['authz'] });
    this.initialized = false;
  }

  /**
   * Imperative API to check if an action is allowed on a subject
   *
   * @param action - The action to perform
   * @param subject - The subject/resource type
   * @param record - Optional record for instance-based checks
   * @returns Promise resolving to true if allowed, false otherwise
   *
   * @example
   * const canApprove = await authzClient.can('approve', 'APInvoice');
   * const canUpdate = await authzClient.can('update', 'APInvoice', { id: '123' });
   */
  async can(
    action: Action,
    subject: Subject,
    record?: Record<string, unknown>,
  ): Promise<boolean> {
    const cachedData = this.queryClient.getQueryData<AuthzCanOutput>(
      getAuthzQueryKey({ action, subject, record }),
    );

    if (cachedData) {
      return cachedData.allowed;
    }

    const { allowed } = await orpc.authz.can.call({
      action,
      subject,
      record,
    });

    this.addPermissionToCache({ action, subject, record }, allowed);

    return allowed;
  }

  /**
   * Initialize the cache with base ability rules from the backend
   *
   * Only rules without conditions are cached (base abilities).
   * Rules with conditions require instance-specific checks and cannot be pre-cached.
   *
   * @param rules - Ability rules from getCurrentUser response
   */
  initializeFromAbilityRules(rules: AbilityRulesDTO): void {
    if (this.initialized) {
      return;
    }

    // Filter to only unconditional rules (base abilities)
    // Rules with conditions cannot be pre-cached
    const baseAbilities = rules.filter((rule) => !rule.conditions);

    // Convert to cache entries
    const cacheEntries: [
      { action: Action; subject: Subject; record?: Record<string, unknown> },
      boolean,
    ][] = baseAbilities.map((rule) => [
      { action: rule.action, subject: rule.subject, record: undefined },
      !rule.inverted, // inverted rules are denials
    ]);

    // Bulk insert into cache
    this.addPermissionsToCache(cacheEntries);

    this.initialized = true;
  }

  addPermissionToCache(
    {
      action,
      subject,
      record,
    }: { action: Action; subject: Subject; record?: Record<string, unknown> },
    allowed: boolean,
  ) {
    const queryKey = getAuthzQueryKey({ action, subject, record });

    // Set the data in cache
    // The cache entry will be considered fresh until AUTHZ_CACHE_TIME_MS passes
    // This is enforced by useCan's staleTime configuration
    this.queryClient.setQueryData<AuthzCanOutput>(
      queryKey,
      {
        allowed,
      },
      {
        // Set the updated timestamp to now, so staleness calculation starts from this moment
        updatedAt: Date.now(),
      },
    );
  }

  addPermissionsToCache(
    permissions: [
      { action: Action; subject: Subject; record?: Record<string, unknown> },
      boolean,
    ][],
  ) {
    permissions.forEach(([permissionWithRecord, allowed]) => {
      this.addPermissionToCache(permissionWithRecord, allowed);
    });
  }

  /**
   * Synchronously check if a permission is cached.
   * Returns the cached value or undefined if not in cache.
   *
   * This is useful for synchronous permission checks (e.g., route filtering)
   * when the cache has been pre-populated via initializeFromAbilityRules.
   *
   * @param action - The action to check
   * @param subject - The subject/resource type
   * @returns boolean if cached, undefined if not in cache
   */
  getCachedPermission(action: Action, subject: Subject): boolean | undefined {
    const cachedData = this.queryClient.getQueryData<AuthzCanOutput>(
      getAuthzQueryKey({ action, subject }),
    );
    return cachedData?.allowed;
  }
}

export const authzClient = new AuthzClient(queryClient);
export type { AuthzClient };
